import { useEffect, useState } from 'react';

import { LoadStopTypes, NormalizedLoad } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { ApptConfirmationChanges } from 'types/suggestions/LoadSuggestions';

// Nested accordion values inside Pickup / Consignee stop forms.
export enum LoadStopNestedSection {
  AdditionalDetails = 'additional-details',
  PickupAppointment = 'pickup-appointment',
  ConsigneeAppointment = 'consignee-appointment',
  Address = 'address',
  Appointment = 'appointment',
}

// True when a suggestion writes appointment/date fields under Pickup or Consignee.
export function suggestionTouchesStopDetails(
  suggestion: Maybe<GenericSuggestion>,
  stop: LoadStopTypes
): boolean {
  if (!suggestion) {
    return false;
  }

  if (suggestion.pipeline === SuggestionPipelines.ApptConfirmation) {
    const suggested = suggestion.suggested as Partial<ApptConfirmationChanges>;
    if (stop === LoadStopTypes.Pickup) {
      return !!suggested.pickupApptTime;
    }
    return !!suggested.dropoffApptTime;
  }

  if (suggestion.pipeline === SuggestionPipelines.LoadBuilding) {
    const load = suggestion.suggested as Partial<NormalizedLoad>;
    if (stop === LoadStopTypes.Pickup) {
      const pickup = load.pickup;
      return !!(
        pickup?.apptStartTime ||
        pickup?.apptEndTime ||
        pickup?.readyTime
      );
    }
    const consignee = load.consignee;
    return !!(
      consignee?.apptStartTime ||
      consignee?.apptEndTime ||
      consignee?.mustDeliver
    );
  }

  return false;
}

// Controlled single-collapsible accordion (e.g. Tai "Additional details").
export function useOpenNestedStopDetailsOnSuggestion(
  clickedSuggestion: Maybe<GenericSuggestion>,
  stop: LoadStopTypes,
  sectionValue: LoadStopNestedSection
): [Undef<string>, (value: string) => void] {
  const [openValue, setOpenValue] = useState<Undef<string>>(undefined);

  useEffect(() => {
    if (suggestionTouchesStopDetails(clickedSuggestion, stop)) {
      setOpenValue(sectionValue);
    }
  }, [clickedSuggestion?.id, stop, sectionValue]);

  return [openValue, setOpenValue];
}

// Controlled multi accordion that re-opens a nested section when a suggestion touches it.
export function useEnsureNestedSectionOpenOnSuggestion(
  clickedSuggestion: Maybe<GenericSuggestion>,
  stop: LoadStopTypes,
  sectionValue: LoadStopNestedSection,
  initialOpen: LoadStopNestedSection[] = [sectionValue]
): [string[], (value: string[]) => void] {
  const [openValues, setOpenValues] = useState<string[]>(initialOpen);

  useEffect(() => {
    if (!suggestionTouchesStopDetails(clickedSuggestion, stop)) {
      return;
    }
    setOpenValues((prev) => {
      if (prev.includes(sectionValue)) {
        return prev;
      }
      return [...prev, sectionValue];
    });
  }, [clickedSuggestion?.id, stop, sectionValue]);

  return [openValues, setOpenValues];
}
